import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { AlertCircle, Bug, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const ReportIssue = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    user_name: "",
    user_email: "",
    issue_type: "bug",
    page_url: window.location.href,
    subject: "",
    description: "",
    website: "", // honeypot
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { error } = await supabase.functions.invoke("submit-issue-report", {
        body: {
          ...formData,
          browser_info: navigator.userAgent,
        },
      });

      if (error) throw error;

      toast({
        title: "Issue reported successfully!",
        description: "Thank you for helping us improve. We'll look into this issue as soon as possible.",
        duration: 5000,
      });

      // Reset form
      setFormData({
        user_name: "",
        user_email: "",
        issue_type: "bug",
        page_url: window.location.href,
        subject: "",
        description: "",
        website: "",
      });
    } catch (error: any) {
      console.error("Error submitting issue report:", error);
      toast({
        title: "Failed to submit report",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Report an Issue | AI Blog</title>
        <meta
          name="description"
          content="Help us improve by reporting bugs, UI issues, or other problems you encounter on our website."
        />
      </Helmet>

      <div className="min-h-screen bg-background py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <Bug className="mx-auto h-12 w-12 text-primary mb-4" />
            <h1 className="text-4xl font-bold text-foreground mb-4">
              Report an Issue
            </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Found a bug, UI issue, or something not working correctly? Let us know and we'll fix it!
          </p>
        </div>

        <Link to="/" className="inline-block mb-6">
          <Button variant="ghost" size="sm" className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Blog
          </Button>
        </Link>

        <div className="bg-card rounded-lg shadow-lg p-8 border border-border">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Honeypot field - hidden from users */}
              <input
                type="text"
                name="website"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="hidden"
                tabIndex={-1}
                autoComplete="off"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="user_name">Your Name (Optional)</Label>
                  <Input
                    id="user_name"
                    type="text"
                    value={formData.user_name}
                    onChange={(e) => setFormData({ ...formData, user_name: e.target.value })}
                    placeholder="John Doe"
                    maxLength={100}
                  />
                </div>

                <div>
                  <Label htmlFor="user_email">
                    Email <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="user_email"
                    type="email"
                    value={formData.user_email}
                    onChange={(e) => setFormData({ ...formData, user_email: e.target.value })}
                    placeholder="john@example.com"
                    required
                    maxLength={255}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="issue_type">
                  Issue Type <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.issue_type}
                  onValueChange={(value) => setFormData({ ...formData, issue_type: value })}
                >
                  <SelectTrigger id="issue_type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bug">Bug / Error</SelectItem>
                    <SelectItem value="ui_issue">UI / Display Issue</SelectItem>
                    <SelectItem value="performance">Performance Issue</SelectItem>
                    <SelectItem value="content_error">Content Error</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="page_url">Page URL (Where the issue occurred)</Label>
                <Input
                  id="page_url"
                  type="url"
                  value={formData.page_url}
                  onChange={(e) => setFormData({ ...formData, page_url: e.target.value })}
                  placeholder="https://example.com/page"
                  maxLength={500}
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Auto-filled with current page, but you can edit it
                </p>
              </div>

              <div>
                <Label htmlFor="subject">
                  Subject <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="subject"
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Brief description of the issue"
                  required
                  maxLength={200}
                />
                <p className="text-sm text-muted-foreground mt-1">
                  {formData.subject.length}/200 characters
                </p>
              </div>

              <div>
                <Label htmlFor="description">
                  Detailed Description <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Please describe the issue in detail. Include steps to reproduce if applicable."
                  required
                  maxLength={2000}
                  rows={6}
                  className="resize-none"
                />
                <p className="text-sm text-muted-foreground mt-1">
                  {formData.description.length}/2000 characters
                </p>
              </div>

              <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
                <AlertCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium text-foreground mb-1">Tips for reporting issues:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Be as specific as possible</li>
                    <li>Include steps to reproduce the issue</li>
                    <li>Mention your browser and device if relevant</li>
                    <li>Screenshots help (you can send them via email)</li>
                  </ul>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting || !formData.user_email || !formData.subject || !formData.description}
              >
                {isSubmitting ? "Submitting..." : "Submit Issue Report"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReportIssue;
